/**
 * MemberList component
 * Displays project members with their roles
 */

import PropTypes from 'prop-types'
import Button from '../common/Button'

const MemberList = ({
  members,
  userRole,
  currentUserId,
  onRemoveMember,
  onUpdateRole,
  removingMemberId,
  onConfirmRemove,
  onCancelRemove,
}) => {
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800'
      case 'editor':
        return 'bg-blue-100 text-blue-800'
      case 'viewer':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const canManageMembers = userRole === 'owner'

  return (
    <div className="space-y-3">
      {members.map((member) => {
        const isOwner = member.role === 'owner'
        const isCurrentUser = member.user.id === currentUserId
        const isRemoving = removingMemberId === member.id

        return (
          <div key={member.id}>
            <div
              className={`flex items-center justify-between p-4 bg-white border rounded-lg transition-colors ${
                isRemoving ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-700 font-semibold text-sm">
                        {member.user.first_name?.[0]}
                        {member.user.last_name?.[0]}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {member.user.full_name}
                      {isCurrentUser && <span className="ml-2 text-xs text-gray-500">(You)</span>}
                    </p>
                    <p className="text-sm text-gray-500 truncate">{member.user.email}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 ml-4">
                {canManageMembers && !isOwner ? (
                  <select
                    value={member.role}
                    onChange={(e) => onUpdateRole(member.id, e.target.value)}
                    className="text-xs font-medium rounded-full px-3 py-1 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={isRemoving}
                  >
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                ) : (
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(member.role)}`}
                  >
                    {member.role}
                  </span>
                )}

                {canManageMembers && !isOwner && !isRemoving && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRemoveMember(member.id)}
                    className="text-red-600 hover:text-red-700 hover:border-red-300"
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>

            {/* Inline Confirmation */}
            {isRemoving && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                <p className="text-sm text-red-800">
                  Remove <span className="font-medium">{member.user.full_name}</span> from this
                  project?
                </p>
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onCancelRemove}
                    className="bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onConfirmRemove(member.id)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

MemberList.propTypes = {
  members: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      role: PropTypes.oneOf(['owner', 'editor', 'viewer']).isRequired,
      user: PropTypes.shape({
        id: PropTypes.number.isRequired,
        email: PropTypes.string.isRequired,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        full_name: PropTypes.string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  userRole: PropTypes.oneOf(['owner', 'editor', 'viewer']).isRequired,
  currentUserId: PropTypes.number,
  onRemoveMember: PropTypes.func.isRequired,
  onUpdateRole: PropTypes.func.isRequired,
  removingMemberId: PropTypes.string,
  onConfirmRemove: PropTypes.func.isRequired,
  onCancelRemove: PropTypes.func.isRequired,
}

export default MemberList
